import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { trimText } from 'utils/functions/general';
import { advertStatusEnum, advertStatusColorEnum } from 'modules/adverts/enums';
import advertsStrings from 'modules/adverts/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemRow,
  ItemWrapper,
  ItemText,
  ItemSubText,
  ItemClick,
  ItemBtn,
  ItemStatus,
  ItemTag,
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './AdvertsList.style';

const AdvertActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {advertsStrings.advertsList.actionPopupTitle(
            advertStatusEnum.getKeyByValue(actionStatus),
          )}
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {advertsStrings.advertsList.actionPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {advertsStrings.advertsList.actionPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
AdvertActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionStatus: PropTypes.number.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

const AdvertsList = ({
  advertsListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  handleTextToCopyToClipboardClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const advertsGridHeaderList = advertsStrings.advertsList.advertsGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(advertsGridHeaderList).map(advertsGridHeaderKey => (
            <Header key={advertsGridHeaderKey}>
              {advertsGridHeaderList[advertsGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {advertsListArr.map(advert => {
            const advertKey = trimText(advert.adItemKey, 18);
            const advertLandingValue = (advert.clickUrl && trimText(advert.clickUrl, 30)) || '';
            const {
              adItemStatusDto: { adItemStatusId: advertStatusId },
            } = advert;
            const advertStatusStr = advertStatusEnum.getKeyByValue(advertStatusId);
            const { color = colors.white, backgroundColor = colors.greenMain } =
              advertStatusColorEnum(colors)[advertStatusId] || {};
            const { adItemContent } = advert;
            const {
              metrics: { impressions, clicks, sessions, ctr },
            } = advert;
            // const clicksPerImpression = impressions && clicks / impressions;

            return (
              <React.Fragment key={advert.adItemKey}>
                <ItemColumn>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(advert.adItemKey)}>
                    {advertKey}
                  </ItemClick>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{advert.adItemName}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemStatus color={color} backgroundColor={backgroundColor}>
                    {advertStatusStr}
                  </ItemStatus>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{advert.campaign.advertiser.name}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{advert.campaign.campaignName}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  {(adItemContent &&
                    adItemContent.map(product => (
                      <ItemTag key={product.productKey}>{product.productNameEn}</ItemTag>
                    ))) || (
                    <ItemClick onClick={() => handleTextToCopyToClipboardClick(advert.clickUrl)}>
                      {advertLandingValue}
                    </ItemClick>
                  )}
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{impressions}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{clicks}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemWrapper>
                    {/* <ItemText>{clicksPerImpression.toFixed(2)}</ItemText>
                    <SectionDivider gap={2} /> */}
                    <ItemSubText>{`${ctr.toFixed(1)} %`}</ItemSubText>
                  </ItemWrapper>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{sessions}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{0}</ItemText>
                </ItemColumn>
                <ItemRow>
                  {advertStatusId === advertStatusEnum.Inactive && (
                    <ItemBtn
                      as="button"
                      icon={'"\\e908"'}
                      onClick={() =>
                        handleActionBtnClick(advert.adItemKey, advertStatusEnum.Running)
                      }
                    />
                  )}
                  {advertStatusId === advertStatusEnum.Running && (
                    <ItemBtn
                      as="button"
                      backgroundColor={colors.grey005}
                      icon={'"\\e91f"'}
                      onClick={() =>
                        handleActionBtnClick(advert.adItemKey, advertStatusEnum.Inactive)
                      }
                    />
                  )}
                </ItemRow>
                <Divider />
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <AdvertActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

AdvertsList.propTypes = {
  advertsListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.number.isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

AdvertsList.defaultProps = {};

export default AdvertsList;
